import { ArrowDownToLine, CalendarPlus, FilePlus2, Globe, Globe2, KeyRound, Lock, Receipt, RotateCcw, Search, Server, Settings, ShieldCheck, ShoppingCart, Tags, UserCog, UserPlus, UserSquare2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { api, type SearchHit, type SearchHitKind } from '../../api';
import type { Invoice, Order } from '../../api/areas/commerce';
import { useAccess } from '../../lib/access';
import { classifyQuery, shortKindLabel } from '../../lib/classifyQuery';
import { commandTarget, DOMAIN_ACTIONS, parseDomainCommand, type DomainCommand } from '../../lib/commandVerbs';
import { fold, matches } from '../../lib/fold';
import { orderLabel } from '../../lib/labels';
import { hitTarget } from '../../pages/SearchPage';
import { useHubLabels } from '../../nav/hubLabels';
import { HUBS, hubUrl, LEGACY_REDIRECTS, type HubId } from '../../nav/hubs';
import { HUB_ICONS, NAV_ICONS } from '../../nav/icons';
import { useSettingsItemText, useVisibleSettings } from '../../pages/hubs/SettingsIndexPage';
import type { Tld } from '../../api/areas/registry';

type Lookup = (key: string, options?: { defaultValue?: string }) => string;

interface Jump {
  key: string;
  label: string;
  context: string;
  to: string;
  keywords: string;
  hub: HubId;
  icon?: typeof Search;
}

const ACTION_ICONS: Record<DomainCommand, typeof Search> = {
  renew: CalendarPlus,
  nameservers: Server,
  dnssec: ShieldCheck,
  lock: Lock,
  transferCode: KeyRound,
  registrant: UserCog,
  restore: RotateCcw,
  transferIn: ArrowDownToLine,
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A label or a domain name ("tino", "tino.vn", "tên-miền.vn"): offered to "Tra cứu tên miền". */
const NAME_RE = /^[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,62})(?:\.[\p{L}\p{N}-]{1,63})*\.?$/u;
const MAX_HITS = 8;

/**
 * Ctrl/⌘ K (or "/"): one box to open anything.
 * - Objects: domains, customers, subjects, contacts, transfers, dossiers and registry
 *   transactions by name or id (the search API), and recent orders and invoices by domain,
 *   customer or invoice number.
 * - Every hub tab and sub-tab the user may open, also under the name of the page it replaced.
 * - Quick actions: register a domain, new customer, new subject.
 * Any text can also be sent to the full search page.
 */
export function CommandMenu() {
  const { t } = useTranslation();
  const { t: tn } = useTranslation('nav');
  const { t: td } = useTranslation('domains');
  const { t: tu } = useTranslation('ux');
  const labels = useHubLabels();
  const access = useAccess();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [hits, setHits] = useState<{ q: string; items: SearchHit[] } | null>(null);
  const [commerce, setCommerce] = useState<{ orders: Order[]; invoices: Invoice[] } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const q = query.trim();
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q), 250);
    return () => window.clearTimeout(id);
  }, [q]);

  // Objects from the search API.
  useEffect(() => {
    if (!open || debounced.length < 2) return;
    const ctrl = new AbortController();
    api.search(debounced, { signal: ctrl.signal }).then(
      (r) => setHits({ q: debounced, items: r.results }),
      () => setHits({ q: debounced, items: [] }),
    );
    return () => ctrl.abort();
  }, [open, debounced]);

  // Recent orders and invoices, once per opening, when the user may see them.
  const billing = access.has(['billing.read']);
  useEffect(() => {
    if (!open || !billing || debounced.length < 2 || commerce) return;
    const ctrl = new AbortController();
    Promise.all([api.listOrders({ limit: 100 }, { signal: ctrl.signal }), api.listInvoices({ limit: 100 }, { signal: ctrl.signal })]).then(
      ([o, i]) => setCommerce({ orders: o.items, invoices: i.items }),
      () => setCommerce({ orders: [], invoices: [] }),
    );
    return () => ctrl.abort();
  }, [open, billing, debounced, commerce]);

  // TLDs, once per opening, to offer "Mở TLD .com".
  const tldReader = access.has(['registry.transaction.read', 'registry.tld.manage', 'pricing.manage', 'ops.read']) && !access.client;
  const [tlds, setTlds] = useState<Tld[] | null>(null);
  const typing = q.length >= 2;
  useEffect(() => {
    if (!open || !tldReader || tlds || !typing) return;
    const ctrl = new AbortController();
    api.listTlds({ limit: 500 }, { signal: ctrl.signal }).then(
      (r) => setTlds(r.items),
      () => {
        if (!ctrl.signal.aborted) setTlds([]);
      },
    );
    return () => ctrl.abort();
  }, [open, tldReader, tlds, typing]);

  const settingsItems = useVisibleSettings();
  const settingsText = useSettingsItemText();

  const jumps = useMemo<Jump[]>(() => {
    const tr = tn as unknown as Lookup;
    const oldName = (hub: string, tab: string, sub?: string) =>
      LEGACY_REDIRECTS.filter((r) => r.hub === hub && r.tab === tab && (!sub || r.sub === sub))
        .map((r) => `${tr(`items.${r.from}.label`, { defaultValue: '' })} ${r.from}`)
        .join(' ');
    const icon = (hub: string, tab: string, sub?: string) => {
      const legacy = LEGACY_REDIRECTS.find((r) => r.hub === hub && r.tab === tab && (!sub || r.sub === sub));
      return (legacy && NAV_ICONS[legacy.from]) || HUB_ICONS[hub];
    };
    return HUBS.flatMap((hub) =>
      access.tabs(hub).flatMap((tab) => {
        const hubLabel = labels.hub(hub.id);
        const tabLabel = labels.tab(hub.id, tab.id);
        const own: Jump = { key: `${hub.id}/${tab.id}`, label: tabLabel, context: hubLabel, to: hubUrl(hub.id, tab.id), keywords: `${hubLabel} ${tabLabel} ${oldName(hub.id, tab.id)}`, hub: hub.id, icon: icon(hub.id, tab.id) };
        const subs = (tab.subs ?? []).map<Jump>((sub) => {
          const subLabel = labels.sub(hub.id, tab.id, sub.id);
          return {
            key: `${hub.id}/${tab.id}/${sub.id}`,
            label: subLabel,
            context: `${hubLabel} › ${tabLabel}`,
            to: hubUrl(hub.id, tab.id, sub.id),
            keywords: `${hubLabel} ${tabLabel} ${subLabel} ${oldName(hub.id, tab.id, sub.id)}`,
            hub: hub.id,
            icon: icon(hub.id, tab.id, sub.id),
          };
        });
        return [own, ...subs];
      }),
    );
  }, [access, labels, tn]);

  const onOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o) setCommerce(null);
  };

  const go = (to: string) => {
    onOpenChange(false);
    setQuery('');
    void navigate(to);
  };

  const top = q ? classifyQuery(q).candidates[0] : undefined;
  const objectHits = hits && hits.q === debounced && debounced === q ? hits.items.slice(0, MAX_HITS) : [];
  const orders = q.length >= 2 && commerce ? commerce.orders.filter((o) => o.id === q || matches(`${o.domains.join(' ')} ${o.customer_name}`, q)).slice(0, 4) : [];
  const invoices = q.length >= 2 && commerce ? commerce.invoices.filter((i) => i.id === q || matches(`${i.series}-${i.number} ${i.customer_name} ${i.order_domain ?? ''}`, q)).slice(0, 4) : [];
  const visibleJumps = q ? jumps.filter((j) => matches(j.keywords, q)) : jumps;
  const showActions = !access.client;
  const actions = [
    { id: 'register', label: tn('ui.registerDomain'), icon: Globe, to: '/?action=register', keywords: 'dang ky ten mien register domain' },
    { id: 'customer', label: tn('ui.newCustomer'), icon: UserPlus, to: '/?action=customer', keywords: 'tao khach hang new customer' },
    { id: 'subject', label: tn('ui.newSubject'), icon: UserSquare2, to: '/?action=subject', keywords: 'tao chu the new subject registrant' },
    { id: 'transfer-in', label: tu('command.transferIn'), icon: ArrowDownToLine, to: '/?action=transfer-in', keywords: 'chuyen ten mien ve transfer in nha dang ky khac' },
    ...(tldReader ? [{ id: 'tld', label: tu('command.sellTld'), icon: Tags, to: `${hubUrl('products', 'tlds')}&action=add-tld`, keywords: 'them tld moi ban tld add tld new tld thiet lap tld' }] : []),
  ].filter((a) => !q || matches(`${a.label} ${a.keywords}`, q));
  // "Gia hạn example.vn", "renew example.vn", "đổi ns example.vn": the action on that domain.
  const verb = showActions ? parseDomainCommand(q) : null;
  // Actions on the best domain and customer hits.
  const domainHit = showActions ? objectHits.find((h) => h.kind === 'domain') : undefined;
  const domainName = domainHit ? (domainHit.detail ?? domainHit.label ?? '') : '';
  const customerHit = showActions ? objectHits.find((h) => h.kind === 'customer') : undefined;
  const domainActions = verb ? [verb] : domainName ? DOMAIN_ACTIONS.slice(0, 3).map((action) => ({ action, domain: domainName })) : [];
  const zone = fold(q).replace(/^\.+/, '');
  const tldHits = zone.length >= 2 && tlds ? tlds.filter((x) => x.zone.replace(/^\./, '').startsWith(zone)).slice(0, 4) : [];
  const settingsHits = q.length >= 2 ? settingsItems.filter((x) => { const s = settingsText(x); return matches(`${s.label} ${x.keywords}`, q); }).slice(0, 6) : [];
  const lookup = !access.client && access.has(['domain.read']) && q.length >= 2 && NAME_RE.test(q) && !UUID_RE.test(q);
  const searching = q.length >= 2 && (debounced !== q || !hits || hits.q !== q);

  return (
    <>
      <Button
        variant="outline"
        className="h-9 w-full min-w-0 max-w-96 justify-start gap-2 rounded-lg bg-muted/40 px-3 text-sm font-normal text-muted-foreground shadow-none"
        onClick={() => setOpen(true)}
        aria-label={t('search.label')}
      >
        <Search className="size-4" />
        <span className="hidden truncate sm:inline">{t('search.placeholder')}</span>
        <kbd className="pointer-events-none ml-auto hidden h-5 select-none items-center gap-1 rounded border bg-background px-1.5 font-mono text-[10px] font-medium sm:flex">Ctrl K</kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={onOpenChange} title={t('search.commandTitle')} description={t('search.commandPlaceholder')} shouldFilter={false}>
        <CommandInput placeholder={t('search.commandPlaceholder')} value={query} onValueChange={setQuery} />
        <CommandList>
          <CommandEmpty>{searching ? tn('ui.searching') : t('search.commandEmpty')}</CommandEmpty>
          {q ? (
            <CommandGroup heading={t('search.go')}>
              <CommandItem value={`search ${q}`} onSelect={() => go(`/search?q=${encodeURIComponent(q)}`)}>
                <Search />
                <span className="truncate">{t('search.searchFor', { q })}</span>
                {top ? <span className="ml-auto text-xs text-muted-foreground">{shortKindLabel(top.kind)}</span> : null}
              </CommandItem>
            </CommandGroup>
          ) : null}
          {lookup ? (
            <CommandGroup heading={tn('hubs.domains.tabs.lookup')}>
              <CommandItem value={`lookup ${q}`} onSelect={() => go(hubUrl('domains', 'lookup', undefined, { q }))}>
                <Globe2 />
                <span className="truncate">{td('lookup.commandItem', { q })}</span>
              </CommandItem>
            </CommandGroup>
          ) : null}
          {objectHits.length || orders.length || invoices.length ? (
            <>
              <CommandSeparator />
              <CommandGroup heading={tn('ui.results')}>
                {objectHits.map((hit, i) => {
                  const target = hitTarget(hit, td);
                  if (!target.to) return null;
                  const to = target.to;
                  return (
                    <CommandItem key={`${hit.kind}:${hit.id}:${i}`} value={`hit ${hit.kind} ${hit.id} ${i}`} onSelect={() => go(to)}>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className={target.mono ? 'truncate font-mono text-sm' : 'truncate'}>{target.title}</span>
                        {target.subtitle ? <span className="truncate text-xs text-muted-foreground">{target.subtitle}</span> : null}
                      </span>
                      <span className="ml-2 shrink-0 text-xs text-muted-foreground">{td(`search.kinds.${hit.kind as SearchHitKind}`, { defaultValue: hit.kind })}</span>
                    </CommandItem>
                  );
                })}
                {orders.map((o) => (
                  <CommandItem key={`order:${o.id}`} value={`order ${o.id}`} onSelect={() => go(`/orders/${encodeURIComponent(o.id)}`)}>
                    <ShoppingCart />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate">{orderLabel(o)}</span>
                      <span className="truncate text-xs text-muted-foreground">{tn('ui.orderHit', { detail: o.customer_name })}</span>
                    </span>
                  </CommandItem>
                ))}
                {invoices.map((i) => (
                  <CommandItem key={`invoice:${i.id}`} value={`invoice ${i.id}`} onSelect={() => go(`/invoices/${encodeURIComponent(i.id)}`)}>
                    <Receipt />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-mono text-sm">{`${i.series}-${i.number}`}</span>
                      <span className="truncate text-xs text-muted-foreground">{tn('ui.invoiceHit', { detail: i.customer_name })}</span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          ) : null}
          {billing && UUID_RE.test(q) ? (
            <>
              <CommandSeparator />
              <CommandGroup heading={tn('ui.goTo')}>
                <CommandItem value={`order-id ${q}`} onSelect={() => go(`/orders/${encodeURIComponent(q)}`)}>
                  <ShoppingCart />
                  <span>{tn('ui.openOrderById')}</span>
                </CommandItem>
                <CommandItem value={`invoice-id ${q}`} onSelect={() => go(`/invoices/${encodeURIComponent(q)}`)}>
                  <Receipt />
                  <span>{tn('ui.openInvoiceById')}</span>
                </CommandItem>
              </CommandGroup>
            </>
          ) : null}
          {domainActions.length || customerHit ? (
            <>
              <CommandSeparator />
              <CommandGroup heading={tu('command.actions')}>
                {domainActions.map((a) => {
                  const Icon = ACTION_ICONS[a.action];
                  return (
                    <CommandItem key={`da:${a.action}`} value={`domain-action ${a.action} ${a.domain}`} onSelect={() => go(commandTarget(a))}>
                      <Icon />
                      <span className="truncate">{tu('command.domainAction', { action: tu(`command.domainActions.${a.action}`), domain: a.domain })}</span>
                    </CommandItem>
                  );
                })}
                {customerHit ? (
                  <CommandItem value={`register-for ${customerHit.id}`} onSelect={() => go(`/customers/${encodeURIComponent(customerHit.id)}?action=register`)}>
                    <Globe />
                    <span className="truncate">{tu('command.registerFor', { name: customerHit.label ?? '' })}</span>
                  </CommandItem>
                ) : null}
              </CommandGroup>
            </>
          ) : null}
          {tldHits.length ? (
            <>
              <CommandSeparator />
              <CommandGroup heading={tn('hubs.products.tabs.tlds')}>
                {tldHits.map((x) => (
                  <CommandItem key={`tld:${x.id}`} value={`tld ${x.id}`} onSelect={() => go(`/registry/tlds/${encodeURIComponent(x.id)}`)}>
                    <Tags />
                    <span className="truncate">{tu('command.openTld', { zone: x.zone.replace(/^\./, '') })}</span>
                    <span className="ml-auto truncate pl-2 text-xs text-muted-foreground">{x.registry_name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          ) : null}
          {settingsHits.length ? (
            <>
              <CommandSeparator />
              <CommandGroup heading={tu('command.settings')}>
                {settingsHits.map((x) => (
                  <CommandItem key={`setting:${x.id}`} value={`setting ${x.id}`} onSelect={() => go(x.to)}>
                    <Settings />
                    <span className="truncate">{settingsText(x).label}</span>
                    <span className="ml-auto truncate pl-2 text-xs text-muted-foreground">{tu(`settings.groups.${x.group}.label`)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          ) : null}
          {showActions && actions.length ? (
            <>
              <CommandSeparator />
              <CommandGroup heading={tn('ui.quickActions')}>
                {actions.map((a) => (
                  <CommandItem key={a.id} value={`action ${a.id}`} onSelect={() => go(a.to)}>
                    <a.icon />
                    <span>{a.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          ) : null}
          {visibleJumps.length ? (
            <>
              <CommandSeparator />
              <CommandGroup heading={tn('ui.goTo')}>
                {visibleJumps.map((j) => {
                  const Icon = j.icon ?? FilePlus2;
                  return (
                    <CommandItem key={j.key} value={`jump ${j.key}`} onSelect={() => go(j.to)}>
                      <Icon />
                      <span className="truncate">{j.label}</span>
                      <span className="ml-auto truncate pl-2 text-xs text-muted-foreground">{j.context}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          ) : null}
        </CommandList>
      </CommandDialog>
    </>
  );
}
